import { api } from './api'

/**
 * Typed client for the LingoTalk → lingodb content-library proxy.
 *
 * Every method here hits `/api/patient/library/*` which the Laravel
 * backend mints a JWT for and forwards to the upstream lingodb API.
 * Response shapes mirror lingodb's resources verbatim (see
 * docs/api/README.md in the lingodb repo); we don't reshape on the proxy.
 */

// --- Common shapes -------------------------------------------------------

export interface PresignedMedia {
  url: string
  /** ISO 8601 — typically ~15 min in the future. */
  expires_at: string
  source?: string
  license?: string
}

export interface CursorMeta {
  path: string
  per_page: number
  next_cursor: string | null
  prev_cursor: string | null
}

export interface CursorLinks {
  first: string | null
  last: string | null
  prev: string | null
  next: string | null
}

export interface CursorPage<T> {
  data: T[]
  meta: CursorMeta
  links: CursorLinks
}

// --- Topics --------------------------------------------------------------

export interface TopicSummary {
  id: number
  name: string
  items_count: number
  /** Presigned cover image; null if the topic has no visible items. */
  cover_image: PresignedMedia | null
  created_at: string
  updated_at: string
}

export interface FetchTopicsOptions {
  cursor?: string
  perPage?: number
}

export async function fetchTopics(
  opts: FetchTopicsOptions = {},
): Promise<CursorPage<TopicSummary>> {
  const params: Record<string, string | number> = {}
  if (opts.cursor) params.cursor = opts.cursor
  if (opts.perPage !== undefined) params.per_page = opts.perPage

  const { data } = await api.get<CursorPage<TopicSummary>>('/patient/library/topics', { params })
  return data
}

// --- Items ---------------------------------------------------------------

export type ItemType =
  | 'WORD' | 'PHRASE' | 'SENTENCE' | 'TEXT'
  | 'NEOLOGISM' | 'SYLLABLE' | 'SEGMENT'

/** Difficulty grade — 1=easy, 4=expert. Matches the upstream enum. */
export type Difficulty = 1 | 2 | 3 | 4

export interface ItemSummary {
  id: number
  content: string
  type: ItemType
  ipa: string | null
  difficulty_lingotalk: Difficulty | null
  difficulty_lingophon: Difficulty | null
  image1: PresignedMedia | null
  audio_full: PresignedMedia | null
}

export interface ItemFullMedia {
  image1: PresignedMedia | null
  image2: PresignedMedia | null
  image_help: PresignedMedia | null
  mouth: PresignedMedia | null
  audio_full: PresignedMedia | null
  audio_start: PresignedMedia | null
  audio_first_part: PresignedMedia | null
  audio_description: PresignedMedia | null
  audio_sentence: PresignedMedia | null
  audio_properties: PresignedMedia | null
}

export interface ItemFull extends Omit<ItemSummary, 'image1' | 'audio_full'> {
  status: 'active' | 'review' | 'new'
  is_ambiguous: boolean
  disambiguation: string | null
  is_child_friendly: boolean
  /** Open-ended word-level metadata — full schema in the upstream docs. */
  word: Record<string, unknown> | null
  media: ItemFullMedia
  topics: Array<{ id: number; name: string }>
  categories: Array<{ id: number; name: string }>
  tags: Array<{ id: number; name: string }>
  genera: Array<{ id: number; name: string }>
  casuses: Array<{ id: number; name: string }>
  modes: Array<{ id: number; name: string }>
  persons: Array<{ id: number; name: string }>
  tempuses: Array<{ id: number; name: string }>
  ambiguities: Array<{ id: number; content: string }>
  created_at: string
  updated_at: string
}

export interface FetchTopicItemsOptions {
  cursor?: string
  perPage?: number
}

export async function fetchTopicItems(
  topicId: number,
  opts: FetchTopicItemsOptions = {},
): Promise<CursorPage<ItemSummary>> {
  const params: Record<string, string | number> = {}
  if (opts.cursor) params.cursor = opts.cursor
  if (opts.perPage !== undefined) params.per_page = opts.perPage

  const { data } = await api.get<CursorPage<ItemSummary>>(
    `/patient/library/topics/${topicId}/items`,
    { params },
  )
  return data
}

export interface FetchItemResult {
  status: 200 | 304
  etag: string | null
  /** null when status === 304 (Not Modified). */
  body: { data: ItemFull } | null
}

export async function fetchItem(
  itemId: number,
  etag?: string,
  opts: { bustCache?: boolean } = {},
): Promise<FetchItemResult> {
  const headers: Record<string, string> = {}
  if (etag) headers['If-None-Match'] = etag

  // bustCache appends a unique query param so an intermediate cache
  // (notably WKWebView's URL cache, which can heuristically reuse an
  // ETag'd JSON response) can't hand us back stale presigned URLs.
  const params: Record<string, number> = {}
  if (opts.bustCache) params._ = Date.now()

  const response = await api.get<{ data: ItemFull }>(
    `/patient/library/items/${itemId}`,
    {
      headers,
      params,
      // 304 must not throw — it's a "your cache is still valid" success.
      validateStatus: (s) => s === 200 || s === 304,
    },
  )

  return {
    status: response.status as 200 | 304,
    etag: response.headers['etag'] ?? null,
    body: response.status === 304 ? null : response.data,
  }
}

export interface FetchItemsOptions extends FetchTopicItemsOptions {
  type?: ItemType
  difficulty_lingotalk?: Difficulty
  difficulty_lingophon?: Difficulty
  is_lingo_talk_relevant?: boolean
  is_child_friendly?: boolean
  topics?: number[]
  categories?: number[]
  tags?: number[]
  q?: string
}

export async function fetchItems(opts: FetchItemsOptions = {}): Promise<CursorPage<ItemSummary>> {
  const params: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(opts)) {
    if (v === undefined || v === null) continue
    if (k === 'perPage') {
      params.per_page = v
    } else {
      params[k] = v
    }
  }
  const { data } = await api.get<CursorPage<ItemSummary>>('/patient/library/items', { params })
  return data
}

export type DistractionKind =
  | 'semantic_close' | 'semantic_distant' | 'semantic_unrelated'
  | 'visual'
  | 'phonological_initial' | 'phonological_medial' | 'phonological_final'
  | 'ambiguities'

export async function fetchDistractions(
  itemId: number,
  kind: DistractionKind,
  opts: { cursor?: string; perPage?: number } = {},
): Promise<CursorPage<ItemSummary>> {
  const params: Record<string, string | number> = { kind }
  if (opts.cursor) params.cursor = opts.cursor
  if (opts.perPage !== undefined) params.per_page = opts.perPage

  const { data } = await api.get<CursorPage<ItemSummary>>(
    `/patient/library/items/${itemId}/distractions`,
    { params },
  )
  return data
}
